import { useState, useEffect, Fragment } from 'react';
import { MdClose, MdChevronRight } from 'react-icons/md';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { useAdvisor } from '../context/AdvisorContext';
import './HoldingsModal.css';

const SECTOR_COLORS = {
  'Energy':                 '#f97316',
  'Materials':              '#84cc16',
  'Industrials':            '#0ea5e9',
  'Consumer Discretionary': '#a855f7',
  'Consumer Staples':       '#14b8a6',
  'Health Care':            '#ec4899',
  'Financials':             '#eab308',
  'Information Technology': '#6366f1',
  'Communication Services': '#ef4444',
  'Utilities':              '#8b5cf6',
  'Real Estate':            '#64748b',
  'Other':                  '#94a3b8',
};
const sectorColor = (s) => SECTOR_COLORS[s] || SECTOR_COLORS['Other'];

function HoldingsTable({ title, rows, sleevePct, barColor, sign = 1 }) {
  const { describeCompany, descCache } = useAdvisor();
  const [expandedTicker, setExpandedTicker] = useState(null);
  const [loadingTicker, setLoadingTicker]   = useState(null);

  const portfolioWeights = rows.map(r => r.weight * sleevePct);
  const maxPw = Math.max(...portfolioWeights, 0.01);

  async function toggle(row) {
    if (expandedTicker === row.ticker) { setExpandedTicker(null); return; }
    setExpandedTicker(row.ticker);
    if (!descCache[row.ticker]) {
      setLoadingTicker(row.ticker);
      try { await describeCompany(row.ticker, row.name); }
      finally { setLoadingTicker(prev => prev === row.ticker ? null : prev); }
    }
  }

  return (
    <section className="holdings-col">
      <h3 className="holdings-col__title">{title}</h3>
      <table className="holdings-table">
        <thead>
          <tr>
            <th>Security</th>
            <th className="holdings-table__wt">Portfolio %</th>
            <th className="holdings-table__bar-col"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const pw = portfolioWeights[i];
            const pctLabel = `${(sign * pw).toFixed(2)}%`;
            const barW = (pw / maxPw) * 100;
            const isOpen = expandedTicker === r.ticker;
            return (
              <Fragment key={r.ticker}>
                <tr
                  className={`holdings-table__row ${isOpen ? 'holdings-table__row--open' : ''}`}
                  onClick={() => toggle(r)}
                >
                  <td className="holdings-table__name">
                    <MdChevronRight className="holdings-table__chev" size={16} />
                    <span className="holdings-table__company">{r.name}</span>
                    <span className="holdings-table__ticker"> ({r.ticker})</span>
                  </td>
                  <td className="holdings-table__wt">{pctLabel}</td>
                  <td className="holdings-table__bar-col">
                    <div className="holdings-bar">
                      <div className="holdings-bar__fill" style={{ width: `${barW}%`, background: barColor }} />
                    </div>
                  </td>
                </tr>
                {isOpen && (
                  <tr className="holdings-table__detail">
                    <td colSpan={3}>
                      {loadingTicker === r.ticker
                        ? 'Loading description…'
                        : (descCache[r.ticker] || 'Description unavailable.')}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function SectorDonut({ title, sectors, sign = 1 }) {
  const data = sectors.map(s => ({ ...s, value: Math.abs(s.portfolioPct) }));
  const total = data.reduce((a, b) => a + b.value, 0);

  return (
    <section className="holdings-col">
      <h3 className="holdings-col__title">{title}</h3>
      <div className="sector-donut-wrap">
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="sector"
              cx="50%"
              cy="50%"
              innerRadius={52}
              outerRadius={88}
              paddingAngle={2}
              stroke="#fff"
              strokeWidth={1.5}
            >
              {data.map(d => <Cell key={d.sector} fill={sectorColor(d.sector)} />)}
            </Pie>
            <Tooltip
              contentStyle={{ backgroundColor: '#1c2230', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#e6e8eb', fontSize: 12 }}
              formatter={(v, name) => [`${(sign * v).toFixed(2)}%`, name]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="sector-donut-total">{(sign * total).toFixed(1)}%</div>
      </div>
      <ul className="sector-legend">
        {sectors.map(s => (
          <li key={s.sector} className="sector-legend__item">
            <span className="sector-legend__dot" style={{ background: sectorColor(s.sector) }} />
            <span className="sector-legend__name">{s.sector}</span>
            <span className="sector-legend__val">{(sign * s.portfolioPct).toFixed(2)}%</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

const tooltipStyle = { backgroundColor: '#1c2230', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#e6e8eb', fontSize: 12 };
const fmtDollar = v => `$${Number(v).toLocaleString()}`;
const signed = v => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;

function PerformanceView({ periodData, periodStats }) {
  // Rebase both series to exactly $10,000 at the start of the window.
  const stratBase = periodData[0].Strategy;
  const benchBase = periodData[0]['S&P 500'];
  const rebased = periodData.map(d => ({
    date: d.date,
    Strategy:  (d.Strategy   / stratBase) * 10000,
    'S&P 500': (d['S&P 500'] / benchBase) * 10000,
  }));

  const last = rebased[rebased.length - 1];
  const stratPct = ((last.Strategy   - 10000) / 10000) * 100;
  const benchPct = ((last['S&P 500'] - 10000) / 10000) * 100;

  // Y-axis domain: 95% of the overall min, 105% of the overall max, across both series.
  const all = rebased.flatMap(d => [d.Strategy, d['S&P 500']]);
  const lo  = Math.min(...all) * 0.95;
  const hi  = Math.max(...all) * 1.05;

  // One label per year max for the x-axis
  const yearsSeen = new Set();
  const xTickFmt = (dateStr) => {
    const y = new Date(dateStr).getFullYear();
    if (yearsSeen.has(y)) return '';
    yearsSeen.add(y);
    return String(y);
  };

  return (
    <div className="perf-view">
      <div className="perf-chart-wrap">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={rebased} margin={{ top: 5, right: 16, left: 10, bottom: 0 }}>
            <defs>
              <linearGradient id="perfStratGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#00ff88" stopOpacity={0.28} />
                <stop offset="95%" stopColor="#00ff88" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="perfBenchGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#94a3b8" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#94a3b8" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tickFormatter={xTickFmt} tick={{ fontSize: 11, fill: '#5a6068' }} tickLine={false} interval={Math.max(1, Math.floor(rebased.length / 8))} />
            <YAxis domain={[lo, hi]} tickFormatter={v => `$${(v/1000).toFixed(1)}k`} tick={{ fontSize: 11, fill: '#5a6068' }} tickLine={false} axisLine={false} width={52} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [fmtDollar(Math.round(v)), name]} labelFormatter={l => `Date: ${l}`} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="Strategy" stroke="#00ff88" strokeWidth={2.2} fill="url(#perfStratGrad)" dot={false} />
            <Area type="monotone" dataKey="S&P 500" stroke="#5a6068" strokeWidth={1.5} fill="url(#perfBenchGrad)" dot={false} strokeDasharray="4 2" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="perf-stats">
        <div className="perf-stat">
          <div className="perf-stat__label">Strategy</div>
          <div className="perf-stat__value">{fmtDollar(Math.round(last.Strategy))}</div>
          <div className="perf-stat__delta" style={{ color: stratPct >= 0 ? '#00ff88' : '#ff5b5b' }}>{signed(stratPct)}</div>
        </div>
        <div className="perf-stat">
          <div className="perf-stat__label">S&amp;P 500</div>
          <div className="perf-stat__value">{fmtDollar(Math.round(last['S&P 500']))}</div>
          <div className="perf-stat__delta" style={{ color: benchPct >= 0 ? '#00ff88' : '#ff5b5b' }}>{signed(benchPct)}</div>
        </div>
        <div className="perf-stat">
          <div className="perf-stat__label">Ann. Return</div>
          <div className="perf-stat__value">{signed(periodStats.annualizedReturn)}</div>
        </div>
        <div className="perf-stat">
          <div className="perf-stat__label">Max Drawdown</div>
          <div className="perf-stat__value" style={{ color: '#ff5b5b' }}>-{periodStats.maxDrawdown.toFixed(1)}%</div>
        </div>
        <div className="perf-stat">
          <div className="perf-stat__label">Sharpe</div>
          <div className="perf-stat__value">{periodStats.sharpe.toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
}

export default function HoldingsModal() {
  const { holdingsModal, closeHoldings } = useAdvisor();
  // Toggle a body class so global selectors (sidebar etc.) can selectively
  // blur the rest of the page while the modal is open, without affecting
  // the chat panel where the live agent keeps speaking.
  useEffect(() => {
    if (!holdingsModal) return;
    document.body.classList.add('app-modal-open');
    return () => document.body.classList.remove('app-modal-open');
  }, [holdingsModal]);
  if (!holdingsModal) return null;

  const { view, longPct, shortPct } = holdingsModal;
  const titleText =
    view === 'sectors'     ? 'Sector Mix' :
    view === 'performance' ? 'Strategy Performance' :
                             'Portfolio Holdings';

  let subtitle;
  if (view === 'performance') {
    const fromFmt = new Date(holdingsModal.fromDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    const toFmt   = new Date(holdingsModal.toDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    subtitle = `${fromFmt} – ${toFmt}`;
  } else {
    subtitle = new Date(holdingsModal.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  const bodyClass = `holdings-body ${view === 'performance' ? 'holdings-body--single' : ''}`.trim();

  return (
    <div className="holdings-backdrop" onClick={closeHoldings}>
      <div className="holdings-modal" onClick={e => e.stopPropagation()}>
        <header className="holdings-header">
          <div>
            <h2 className="holdings-title">{titleText}</h2>
            <p className="holdings-subtitle">{subtitle}</p>
          </div>
          <button className="holdings-close" onClick={closeHoldings} aria-label="Close">
            <MdClose size={22} />
          </button>
        </header>

        <div className={bodyClass}>
          {view === 'performance' ? (
            <PerformanceView
              periodData={holdingsModal.periodData}
              periodStats={holdingsModal.periodStats}
            />
          ) : view === 'sectors' ? (
            <>
              <SectorDonut
                title={`Long sleeve (${longPct}% of portfolio)`}
                sectors={holdingsModal.longSectors}
              />
              <SectorDonut
                title={`Short sleeve (${shortPct}% of portfolio)`}
                sectors={holdingsModal.shortSectors}
                sign={-1}
              />
            </>
          ) : (
            <>
              <HoldingsTable
                title={`Long sleeve (${longPct}% of portfolio)`}
                rows={holdingsModal.longs}
                sleevePct={longPct}
                barColor="#00ff88"
              />
              <HoldingsTable
                title={`Short sleeve (${shortPct}% of portfolio)`}
                rows={holdingsModal.shorts}
                sleevePct={shortPct}
                barColor="#ff5b5b"
                sign={-1}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
